import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatPhoneNumber } from "../../utils/strings";

import { agentEnvironmentActions } from "../../redux/reducers/agentEnvironmentSlice";
import { AgentEnvironmentVariable, agentEnvironmentVariableActions } from "../../redux/reducers/agentEnvironmentVariableSlice";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  CheckboxField,
  Dialog,
  DropdownField,
  FieldLabel,
  InputField,
  SwitchField,
  Text,
  Tooltip,
  View,
} from "../../components/base";
import { organizationPhoneNumberActions } from "../../redux/reducers/organizationPhoneNumberSlice";
import { organizationTelephonyActions } from "../../redux/reducers/organizationTelephonySlice";
import { PhoneNumberAssignmentsDialog } from "../../components/agents/PhoneNumberAssignmentsDialog";
import { CreateAgentEnvironmentDialog } from "../../components/agents/CreateAgentEnvironmentDialog";
import { EmptyMessage } from "../../components/EmptyMessage";
import { ErrorFallbackSettingsDialog } from "../../components/agents/ErrorFallbackSettingsDialog";
import { featureActions } from "../../redux/reducers/featureSlice";
import { getGradientForName } from "../../utils/gradients";
import { Icon } from "../../components/base/Icon";
import { PrivacySettingsDialog } from "../../components/agents/PrivacySettingsDialog";
import { RegionSettingsDialog } from "../../components/agents/RegionSettingsDialog";
import { StartupSettingsDialog } from "../../components/agents/StartupSettingsDialog";
import { agentFunctionActions } from "../../redux/reducers/agentFunctionSlice";
import { useDebouncedCallback } from "use-debounce";
import { useAuth } from "../../hooks/useAuth";
import { STTSettingsDialog } from "../../components/agents/STTSettingsDialog";
import { WebhookSettingsDialog } from "../../components/agents/WebhookSettingsDialog";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { useSearchParam } from "../../hooks/useSearchParam";
import { VariablesForm } from "../../components/VariablesForm";
import { useSTTProviders } from "../../hooks/useSTTProviders";
import { useApiRequest } from "../../hooks/useApiRequest";
import { isSOC2Attested } from "../../utils/soc2";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: { duration: 0.2 },
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
};

export const AgentEnvironmentsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const { isAdmin } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSTTSettingsDialog, setShowSTTSettingsDialog] = useState(false);
  const [showErrorFallbackSettingsDialog, setShowErrorFallbackSettingsDialog] = useState(false);
  const [showStartupSettingsDialog, setShowStartupSettingsDialog] = useState(false);
  const [showPrivacySettingsDialog, setShowPrivacySettingsDialog] = useState(false);
  const [showRegionSettingsDialog, setShowRegionSettingsDialog] = useState(false);
  const [showWebhookSettingsDialog, setShowWebhookSettingsDialog] = useState(false);
  const [showPhoneAssignmentsDialog, setShowPhoneAssignmentsDialog] = useState(false);
  const [showOptimizerDialog, setShowOptimizerDialog] = useState(false);
  const [showViewOptimizerDialog, setShowViewOptimizerDialog] = useState(false);
  const environmentId = useSearchParam("e");
  const openModal = useSearchParam("openModal");
  const agentEnvironmentsFormData = useAppSelector((state) => state.feature.agentEnvironmentsFormData);
  const agentEnvironmentVariables = useAppSelector((state) =>
    Object.values(state.agentEnvironmentVariable.entities).filter((v) => v.agentEnvironmentId === environmentId),
  );
  const environments = useAppSelector((state) => Object.values(state.agentEnvironment.entities).filter((env) => env.agentId === agentId));
  const currentEnvironment = useAppSelector((state) => state.agentEnvironment.entities[environmentId]);
  const agentFunctions = useAppSelector((state) => Object.values(state.agentFunction.entities).filter((func) => func.agentId === agentId));
  const { organizationId } = useEffectiveOrganization();
  const { getAllModels, getProviderForModel } = useSTTProviders();
  const isFunctionsLoading = useAppSelector((state) => state.agentFunction.isLoading);
  const functionOptions = agentFunctions.map((f) => ({
    label: <Text className="text-gray-900 dark:text-gray-100">{f.name || `Function ${f.id.substring(0, 8)}...`}</Text>,
    value: f.id,
  }));
  const [functionNameMap, setFunctionNameMap] = useState<Record<string, string>>({});
  const functionIdToName = (fid?: string) => functionNameMap[fid || ""] || agentFunctions.find((f) => f.id === fid)?.name || fid || "";

  // Optimizer form state
  const [startingFunctionId, setStartingFunctionId] = useState<string | undefined>(undefined);
  const [copyFunction, setCopyFunction] = useState<boolean>(true);
  const [approvalMode, setApprovalMode] = useState<string>("approval_required");
  const [slowRolloutAutoDeploy, setSlowRolloutAutoDeploy] = useState<boolean>(false);
  const [slowRolloutPercent, setSlowRolloutPercent] = useState<number>(5);
  const [notifyEmails, setNotifyEmails] = useState<string>("");
  const [focusPrompt, setFocusPrompt] = useState<string>("");
  const [selectedFunctionId, setSelectedFunctionId] = useState<string | undefined>(undefined);
  const [lastUpdatedFunctionId, setLastUpdatedFunctionId] = useState<string | undefined>(undefined);

  const orgPhoneNumbers = useAppSelector((state) => Object.values(state.organizationPhoneNumbers.entities));

  const { fetch: createOptimizer, isLoading: isCreatingOptimizer } = useApiRequest<any>();
  const { fetch: fetchOptimizer, isLoading: isLoadingOptimizer } = useApiRequest<{ data: any }>();
  const { fetch: updateOptimizer, isLoading: isUpdatingOptimizer } = useApiRequest<any>();
  const [optimizerDetails, setOptimizerDetails] = useState<any | null>(null);

  const resetOptimizerForm = () => {
    setStartingFunctionId(undefined);
    setCopyFunction(true);
    setApprovalMode("approval_required");
    setSlowRolloutAutoDeploy(false);
    setSlowRolloutPercent(5);
    setNotifyEmails("");
    setFocusPrompt("");
  };

  const onCreateOptimizer = async () => {
    if (!agentId || !environmentId) return;

    // Find the newest function by createdAt timestamp
    const newestFunction = agentFunctions.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    if (!newestFunction) {
      console.error("No functions found for this agent");
      return;
    }

    const body: Record<string, any> = {
      agentEnvironmentId: environmentId,
      startingFunctionId: newestFunction.id,
      approvalMode,
      copyFunction,
      notifyEmails: notifyEmails
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean),
      focusPrompt: focusPrompt || undefined,
    };
    if (approvalMode === "slow_rollout") {
      body.approvalData = {
        rolloutPercent: Math.max(0, Math.min(1, (Number(slowRolloutPercent) || 0) / 100)),
        autoDeploy: !!slowRolloutAutoDeploy,
      };
    }

    await createOptimizer(`/v1/agents/${agentId}/optimizers`, {
      method: "POST",
      body,
    });
    // Refresh environments so optimizerId is reflected for the button label
    dispatch(agentEnvironmentActions.get({ agentId } as any) as any);
    // Refresh functions so the copied function appears in the list
    dispatch(agentFunctionActions.get({ agentId } as any) as any);
    resetOptimizerForm();
    setShowOptimizerDialog(false);
  };

  const onChangeEnvironmentHandler = (key: string) => (value: unknown) => {
    if (key === "sttEndpointing") {
      value = isNaN(Number(value)) ? 300 : Number(value);
    }
    if (key === "ringingEnabled") {
      value = String(value) === "true";
    }
    if (key === "recordingEnabled") {
      value = String(value) === "true";
    }

    dispatch(
      featureActions.setAgentEnvironmentsFormData({
        ...agentEnvironmentsFormData,
        [key]: value,
      }),
    );
  };

  const onChangeEnvironmentVariable = useDebouncedCallback((updatedVariable: Partial<AgentEnvironmentVariable>) => {
    dispatch(
      agentEnvironmentVariableActions.updateById({
        ...updatedVariable,
        agentId,
        agentEnvironmentId: environmentId,
      }),
    );
  }, 1000);

  const onClickAddEnvironmentVariable = () => {
    dispatch(
      agentEnvironmentVariableActions.create({
        agentId,
        agentEnvironmentId: environmentId,
        name: "",
        value: "",
      }),
    );
  };

  const onClickDeleteEnvironmentVariable = async (id: string) => {
    await dispatch(
      agentEnvironmentVariableActions.deleteById({
        agentId,
        agentEnvironmentId: environmentId,
        id,
      }),
    );
  };

  useEffect(() => {
    if (currentEnvironment) {
      dispatch(featureActions.setAgentEnvironmentsFormData(currentEnvironment));
      dispatch(agentEnvironmentVariableActions.get({ agentId: agentId || "", agentEnvironmentId: environmentId }));
    }
  }, [currentEnvironment]);

  // Handle opening modals from URL param (used by command palette)
  useEffect(() => {
    if (!openModal) return;

    // If no environment selected but environments exist, auto-select first one
    if (!environmentId && environments.length > 0) {
      navigate(`/agents/${agentId}/environments?openModal=${openModal}&e=${environments[0].id}`, { replace: true });
      return;
    }

    // Open the modal once environment is loaded
    if (currentEnvironment) {
      // Close all modals first
      setShowSTTSettingsDialog(false);
      setShowStartupSettingsDialog(false);
      setShowErrorFallbackSettingsDialog(false);
      setShowPrivacySettingsDialog(false);
      setShowWebhookSettingsDialog(false);
      setShowRegionSettingsDialog(false);
      setShowPhoneAssignmentsDialog(false);

      // Open the requested modal
      switch (openModal) {
        case "stt":
          setShowSTTSettingsDialog(true);
          break;
        case "startup":
          setShowStartupSettingsDialog(true);
          break;
        case "fallback":
          setShowErrorFallbackSettingsDialog(true);
          break;
        case "privacy":
          setShowPrivacySettingsDialog(true);
          break;
        case "webhook":
          setShowWebhookSettingsDialog(true);
          break;
        case "region":
          setShowRegionSettingsDialog(true);
          break;
        case "phone":
          setShowPhoneAssignmentsDialog(true);
          break;
      }
      // Clear the openModal param from URL after opening
      navigate(`/agents/${agentId}/environments?e=${environmentId}`, { replace: true });
    }
  }, [openModal, currentEnvironment, environments, agentId, environmentId, navigate]);

  useEffect(() => {
    if (agentId) {
      dispatch(agentFunctionActions.get({ agentId } as any) as any);
    }
  }, [agentId, dispatch]);

  useEffect(() => {
    if (organizationId) {
      dispatch(organizationPhoneNumberActions.get({}) as any);
      dispatch(organizationTelephonyActions.get({}) as any);
    }
  }, [organizationId, dispatch]);

  // Derive phone assignments per environment directly from Redux
  const phoneNumbersByEnv = orgPhoneNumbers.reduce<Record<string, typeof orgPhoneNumbers>>((acc, pn) => {
    if (pn.agentEnvironmentId) {
      if (!acc[pn.agentEnvironmentId]) acc[pn.agentEnvironmentId] = [];
      acc[pn.agentEnvironmentId].push(pn);
    }
    return acc;
  }, {});

  const gradient = getGradientForName(agentEnvironmentsFormData?.name || "");
  const currentDeployedFunction = agentFunctions.find((func) => func.id === currentEnvironment?.functionId);

  return (
    <View className="flex flex-col flex-1 py-2 px-4 mb-2 overflow-hidden min-w-0">
      {environments.length === 0 ? (
        <EmptyMessage
          title="No Environments Found"
          description="Create your first environment to get started. Environments help you manage different configurations for your agent."
          iconName="grid"
          buttonText="Create Your First Environment"
          onClickAdd={() => setShowCreateDialog(true)}
          className="h-full mx-4 my-8"
        />
      ) : environmentId ? (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="flex flex-col flex-1 space-y-4 overflow-hidden"
        >
          <View className="flex flex-col flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700">
            <View className="flex items-center justify-between min-h-[60px] p-2 border-b border-gray-200 dark:border-gray-700 overflow-y-auto">
              <motion.div
                initial={{ x: -60, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="flex items-center space-x-2 min-h-11"
              >
                <button
                  onClick={() => navigate(`/agents/${agentId}/environments`)}
                  type="button"
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon
                    name="close"
                    size={18}
                    className="text-gray-500 dark:text-gray-400"
                  />
                </button>
                {agentEnvironmentsFormData?.name && (
                  <View className="flex items-center gap-3">
                    <View
                      className={`h-9 w-9 rounded-full bg-gradient-to-br ${gradient.from} ${gradient.to} ${gradient.darkFrom} ${gradient.darkTo} flex items-center justify-center`}
                    >
                      <Text className="text-xs font-bold text-white">{agentEnvironmentsFormData.name.substring(0, 2).toUpperCase()}</Text>
                    </View>
                    <View className="flex flex-col">
                      <Text className="text-lg font-semibold text-gray-900 dark:text-white">{agentEnvironmentsFormData.name}</Text>
                      <View className="flex items-center gap-2">
                        <Text className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {agentEnvironmentsFormData.name.toLowerCase().replace(/\s+/g, "-")}
                        </Text>
                        {currentDeployedFunction && (
                          <Text className="text-xs font-medium text-indigo-500 dark:text-indigo-400">Function: {currentDeployedFunction.name}</Text>
                        )}
                        {phoneNumbersByEnv[environmentId]?.length > 0 && (
                          <View className="flex items-center gap-1.5">
                            {phoneNumbersByEnv[environmentId].map((pn: any) => (
                              <Text
                                key={pn.id}
                                className="text-xs font-medium text-green-500 dark:text-green-400"
                              >
                                {formatPhoneNumber(pn.phoneNumber)}
                              </Text>
                            ))}
                          </View>
                        )}
                      </View>
                    </View>
                  </View>
                )}
              </motion.div>
              <View className="flex items-center gap-1">
                <button
                  onClick={() => setShowPhoneAssignmentsDialog(true)}
                  type="button"
                  className="flex flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon
                    name="phone"
                    size={14}
                    className="text-gray-400 dark:text-gray-500"
                  />
                  Phone
                </button>
                <button
                  type="button"
                  onClick={() => setShowStartupSettingsDialog(true)}
                  className="flex flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon
                    name="volume-up"
                    size={14}
                    className="text-gray-400 dark:text-gray-500"
                  />
                  Startup
                </button>
                <button
                  type="button"
                  onClick={() => setShowSTTSettingsDialog(true)}
                  className="flex flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon
                    name="settings"
                    size={14}
                    className="text-gray-400 dark:text-gray-500"
                  />
                  STT
                </button>
                <button
                  type="button"
                  onClick={() => setShowErrorFallbackSettingsDialog(true)}
                  className="flex flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon
                    name="info"
                    size={14}
                    className="text-gray-400 dark:text-gray-500"
                  />
                  Failover
                </button>
                <button
                  type="button"
                  onClick={() => setShowPrivacySettingsDialog(true)}
                  className="flex flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon
                    name="shield"
                    size={14}
                    className="text-gray-400 dark:text-gray-500"
                  />
                  Privacy
                </button>
                <button
                  type="button"
                  onClick={() => setShowRegionSettingsDialog(true)}
                  className="flex flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon
                    name="language"
                    size={14}
                    className="text-gray-400 dark:text-gray-500"
                  />
                  Region
                </button>
                <button
                  type="button"
                  onClick={() => setShowWebhookSettingsDialog(true)}
                  className="flex flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon
                    name="link"
                    size={14}
                    className="text-gray-400 dark:text-gray-500"
                  />
                  Webhook
                </button>
                {(() => {
                  const sttModel = agentEnvironmentsFormData?.sttModel;
                  const sttProvider = sttModel ? getProviderForModel(sttModel) : undefined;
                  const sttAttested = sttProvider ? (sttProvider.soc2Attested ?? isSOC2Attested(sttProvider.id)) : true;

                  const tooltipContent = sttAttested ? "STT is SOC 2 Attested" : "STT is not SOC 2 Attested";

                  return (
                    <Tooltip content={tooltipContent}>
                      <View
                        className={`flex flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium ${
                          sttAttested ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"
                        }`}
                      >
                        <Icon
                          name="shield"
                          size={14}
                        />
                        SOC 2{!sttAttested && " ⚠"}
                      </View>
                    </Tooltip>
                  );
                })()}
                {/* Auto-Optimize button - hidden until optimizer is ready */}
                {/* <Button
                  onClick={async () => {
                    if (currentEnvironment?.optimizerId) {
                      if (!agentId) return;
                      const resp = await fetchOptimizer(`/v1/agents/${agentId}/optimizers/${currentEnvironment.optimizerId}`);
                      const opt = (resp as any)?.data?.data || (resp as any)?.data || null;
                      if (opt) {
                        setOptimizerDetails(opt);
                        setApprovalMode(opt.approvalMode);
                        const ap = opt.approvalData || {};
                        setSlowRolloutAutoDeploy(!!ap.autoDeploy);
                        setSlowRolloutPercent(Math.round(((ap.rolloutPercent ?? 0.05) as number) * 100));
                        setNotifyEmails(((opt.notifyEmails || []) as string[]).join(", "));
                        setFocusPrompt(opt.focusPrompt || "");
                        // Ensure function names are available; fetch any missing
                        const missingIds: string[] = (opt.functionIds || []).filter((fid: string) => !functionIdToName(fid));
                        if (missingIds.length > 0) {
                          const newMap: Record<string, string> = { ...functionNameMap };
                          for (const fid of missingIds) {
                            try {
                              const r = await fetchOptimizer(`/v1/agents/${agentId}/functions/${fid}`);
                              const fn = (r as any)?.data?.data || (r as any)?.data;
                              if (fn && fn.name) newMap[fid] = fn.name;
                            } catch {}
                          }
                          setFunctionNameMap(newMap);
                        }
                      }
                      setShowViewOptimizerDialog(true);
                      setLastUpdatedFunctionId(undefined);
                    } else {
                      setShowOptimizerDialog(true);
                    }
                  }}
                  type="button"
                  variant={currentEnvironment?.optimizerId ? "brand" : "secondary"}
                  className="p-2 flex items-center !shadow-none"
                >
                  <Icon
                    name="sparkles"
                    size={18}
                    className="mr-1"
                  />
                  {currentEnvironment?.optimizerId ? "Auto-Optimize: On" : "Auto-Optimize: Off"}
                </Button> */}
              </View>
            </View>
            {phoneNumbersByEnv[environmentId]?.length > 0 && !currentDeployedFunction && (
              <Alert
                variant="warning"
                className="mx-4 mt-4"
              >
                <AlertTitle className="text-sm">Phone number configured without a function.</AlertTitle>
                <AlertDescription className="text-sm">Calls will fail until a function is deployed to this environment.</AlertDescription>
              </Alert>
            )}
            <View className="flex flex-col flex-1 p-4 space-y-4 overflow-y-auto">
              <InputField
                labelClassName="!w-full"
                className="!w-full"
                label="Name"
                value={agentEnvironmentsFormData?.name || ""}
                onChange={onChangeEnvironmentHandler("name")}
              />
              <FieldLabel
                label="Environment Variables"
                className="w-full flex flex-col flex-1"
              >
                <VariablesForm
                  variables={agentEnvironmentVariables}
                  onChange={onChangeEnvironmentVariable}
                  onClickAdd={onClickAddEnvironmentVariable}
                  onClickDelete={onClickDeleteEnvironmentVariable}
                />
              </FieldLabel>
            </View>
          </View>
        </motion.div>
      ) : (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-4 w-full"
        >
          {environments.map((environment) => {
            const gradient = getGradientForName(environment.name);
            const deployedFunction = agentFunctions.find((func) => func.id === environment.functionId);
            return (
              <motion.div
                key={environment.id}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="w-full px-4 py-2 flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700"
              >
                <View className="flex items-center gap-3">
                  <View
                    className={`h-9 w-9 rounded-full bg-gradient-to-br ${gradient.from} ${gradient.to} ${gradient.darkFrom} ${gradient.darkTo} flex items-center justify-center`}
                  >
                    <Text className="text-xs font-bold text-white">{environment.name.substring(0, 2).toUpperCase()}</Text>
                  </View>
                  <View className="flex flex-col">
                    <Text className="text-lg font-semibold text-gray-900 dark:text-white">{environment.name}</Text>
                    <View className="flex items-center gap-2">
                      <Text className="text-xs text-gray-500 dark:text-gray-400">ID: {environment?.name?.toLowerCase().replace(/\s+/g, "-")}</Text>
                      {deployedFunction && (
                        <Text className="text-xs font-medium text-indigo-500 dark:text-indigo-400">Function: {deployedFunction.displayName}</Text>
                      )}
                      {phoneNumbersByEnv[environment.id]?.length > 0 && !deployedFunction && (
                        <View className="flex items-center gap-1 text-warning-dark dark:text-warning-light">
                          <Icon
                            name="warning-amber"
                            size={14}
                          />
                          <Text className="text-xs font-medium">Calls will fail – no function</Text>
                        </View>
                      )}
                      {phoneNumbersByEnv[environment.id]?.length > 0 && (
                        <View className="flex items-center gap-1.5">
                          <Icon
                            name="phone"
                            size={12}
                            className="text-green-500 dark:text-green-400"
                          />
                          {phoneNumbersByEnv[environment.id].map((pn: any) => (
                            <Text
                              key={pn.id}
                              className="text-xs font-medium text-green-500 dark:text-green-400"
                            >
                              {formatPhoneNumber(pn.phoneNumber)}
                            </Text>
                          ))}
                        </View>
                      )}
                      <View className="flex items-center gap-1">
                        <Icon
                          name={environment.recordingEnabled ? "mic" : "mic-off"}
                          size={14}
                          className={environment.recordingEnabled ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"}
                        />
                        <Text className="text-xs font-medium text-gray-600 dark:text-gray-300">Recording {environment.recordingEnabled ? "On" : "Off"}</Text>
                      </View>
                      <View className="flex items-center gap-1">
                        <Icon
                          name={environment.redactionEnabled ? "lock" : "unlock"}
                          size={14}
                          className={environment.redactionEnabled ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"}
                        />
                        <Text className="text-xs font-medium text-gray-600 dark:text-gray-300">Redaction {environment.redactionEnabled ? "On" : "Off"}</Text>
                      </View>
                      <View className="flex items-center gap-1">
                        <Icon
                          name="activity"
                          size={14}
                          className={environment.tracingEnabled ? "text-indigo-500 dark:text-indigo-400" : "text-gray-400 dark:text-gray-500"}
                        />
                        <Text className="text-xs font-medium text-gray-600 dark:text-gray-300">Tracing {environment.tracingEnabled ? "On" : "Off"}</Text>
                      </View>
                    </View>
                  </View>
                </View>
                <Button
                  variant="neutral"
                  size="auto"
                  onClick={() => navigate(`/agents/${agentId}/environments?e=${environment.id}`)}
                >
                  <Icon
                    name="edit"
                    size={14}
                  />
                  Edit
                </Button>
              </motion.div>
            );
          })}
        </motion.div>
      )}
      <CreateAgentEnvironmentDialog
        agentId={agentId || ""}
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
      />
      <STTSettingsDialog
        isOpen={showSTTSettingsDialog}
        onClose={() => setShowSTTSettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onSave={(config) => {
          dispatch(featureActions.setAgentEnvironmentsFormData({ ...agentEnvironmentsFormData, ...config }));
          if (agentEnvironmentsFormData.id && agentId) {
            dispatch(
              agentEnvironmentActions.updateById({
                id: agentEnvironmentsFormData.id,
                agentId,
                ...config,
                sttFilter: config.sttFilter ?? undefined,
                deploymentAction: "config",
              }),
            );
          }
        }}
        isAdmin={isAdmin}
      />
      <ErrorFallbackSettingsDialog
        isOpen={showErrorFallbackSettingsDialog}
        onClose={() => setShowErrorFallbackSettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onSave={(config) => {
          dispatch(featureActions.setAgentEnvironmentsFormData({ ...agentEnvironmentsFormData, ...config }));
          if (agentEnvironmentsFormData.id && agentId) {
            dispatch(
              agentEnvironmentActions.updateById({
                id: agentEnvironmentsFormData.id,
                agentId,
                ...config,
                errorFallbackType: config.errorFallbackType as "phone" | "webhook" | undefined,
                deploymentAction: "config",
              }),
            );
          }
        }}
      />
      <StartupSettingsDialog
        isOpen={showStartupSettingsDialog}
        onClose={() => setShowStartupSettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onSave={(config) => {
          dispatch(featureActions.setAgentEnvironmentsFormData({ ...agentEnvironmentsFormData, ...config }));
          if (agentEnvironmentsFormData.id && agentId) {
            dispatch(
              agentEnvironmentActions.updateById({
                id: agentEnvironmentsFormData.id,
                agentId,
                ...config,
                deploymentAction: "config",
              }),
            );
          }
        }}
        organizationId={organizationId || ""}
      />
      <PrivacySettingsDialog
        isOpen={showPrivacySettingsDialog}
        onClose={() => setShowPrivacySettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onSave={(config) => {
          dispatch(featureActions.setAgentEnvironmentsFormData({ ...agentEnvironmentsFormData, ...config }));
          if (agentEnvironmentsFormData.id && agentId) {
            dispatch(
              agentEnvironmentActions.updateById({
                id: agentEnvironmentsFormData.id,
                agentId,
                ...config,
                deploymentAction: "config",
              }),
            );
          }
        }}
        organizationId={organizationId || ""}
        isAdmin={isAdmin}
      />
      <RegionSettingsDialog
        isOpen={showRegionSettingsDialog}
        onClose={() => setShowRegionSettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onChange={onChangeEnvironmentHandler}
      />
      <WebhookSettingsDialog
        isOpen={showWebhookSettingsDialog}
        onClose={() => setShowWebhookSettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onChange={onChangeEnvironmentHandler}
      />
      <PhoneNumberAssignmentsDialog
        isOpen={showPhoneAssignmentsDialog}
        onClose={() => setShowPhoneAssignmentsDialog(false)}
        agentId={agentId || ""}
        environmentId={environmentId || ""}
      />
      <Dialog
        isOpen={showOptimizerDialog}
        onClose={() => setShowOptimizerDialog(false)}
        title="Set Up Auto-Optimization"
      >
        <View className="flex flex-col gap-3 p-4">
          <Text className="text-sm text-gray-600 dark:text-gray-400">
            Auto-optimization uses AI to improve your agent's function based on test results. When a test completes, the AI analyzes what worked and generates
            an improved version. Improvements are deployed to this environment based on your approval settings below.
          </Text>
          <View className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-100">
            A copy of your starting function will be created and deployed immediately to collect test data. The "Approval Required" setting applies to future
            AI-generated improvements.
          </View>
          <DropdownField
            label="Starting Function"
            options={functionOptions}
            value={startingFunctionId}
            onChange={setStartingFunctionId}
            placeholder="Search functions by name"
          />
          <CheckboxField
            label="Make a copy of the starting function"
            value={copyFunction}
            onChange={setCopyFunction}
          />
          <DropdownField
            label="Approval Mode"
            options={[
              {
                label: <Text className="text-gray-900 dark:text-gray-100">Approval Required</Text>,
                value: "approval_required",
              },
              {
                label: <Text className="text-gray-900 dark:text-gray-100">Slow Roll-out</Text>,
                value: "slow_rollout",
              },
              {
                label: <Text className="text-gray-900 dark:text-gray-100">Auto-Approve</Text>,
                value: "auto_approve",
              },
            ]}
            value={approvalMode}
            onChange={setApprovalMode}
            placeholder="Select a mode"
          />
          {approvalMode === "slow_rollout" && (
            <View className="flex gap-3">
              <CheckboxField
                label="Auto-deploy new versions"
                value={slowRolloutAutoDeploy}
                onChange={setSlowRolloutAutoDeploy}
              />
              <InputField
                label="Rollout Percent"
                type="number"
                value={slowRolloutPercent}
                onChange={(v) => setSlowRolloutPercent(Math.max(0, Math.min(100, Number(v) || 0)))}
                suffix={<Text className="text-xs text-gray-500">%</Text>}
              />
            </View>
          )}
          <InputField
            label="Notify Emails (comma separated)"
            placeholder="name@example.com, team@company.com"
            value={notifyEmails}
            onChange={setNotifyEmails}
          />
          <InputField
            label="Focus Prompt"
            type="textarea"
            value={focusPrompt}
            onChange={setFocusPrompt}
            placeholder="What should the optimizer focus on?"
          />
          <View className="flex justify-end gap-2 mt-2">
            <Button
              variant="neutral"
              size="auto"
              onClick={() => setShowOptimizerDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              size="auto"
              onClick={onCreateOptimizer}
              disabled={isCreatingOptimizer || !startingFunctionId}
            >
              {isCreatingOptimizer ? "Enabling..." : "Enable"}
            </Button>
          </View>
        </View>
      </Dialog>
      <Dialog
        isOpen={showViewOptimizerDialog}
        onClose={() => setShowViewOptimizerDialog(false)}
        title="Auto-Optimization Settings"
      >
        <View className="flex flex-col max-h-[80vh]">
          <View className="p-4 flex flex-col gap-3 overflow-y-auto flex-1">
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              Auto-optimization uses AI to improve your agent's function based on test results. When a test completes, the AI analyzes what worked and generates
              an improved version. Improvements are deployed to this environment based on your approval settings below.
            </Text>
            {isLoadingOptimizer && <Text className="text-sm text-gray-500">Loading...</Text>}
            {!isLoadingOptimizer && optimizerDetails && (
              <>
                <DropdownField
                  label="Approval Mode"
                  options={[
                    {
                      label: <Text className="text-gray-900 dark:text-gray-100">Approval Required</Text>,
                      value: "approval_required",
                    },
                    {
                      label: <Text className="text-gray-900 dark:text-gray-100">Slow Roll-out</Text>,
                      value: "slow_rollout",
                    },
                    {
                      label: <Text className="text-gray-900 dark:text-gray-100">Auto-Approve</Text>,
                      value: "auto_approve",
                    },
                  ]}
                  value={approvalMode}
                  onChange={setApprovalMode}
                  placeholder="Select a mode"
                />
                {approvalMode === "slow_rollout" && (
                  <View className="flex gap-3">
                    <CheckboxField
                      label={
                        <View>
                          <Text className="font-medium text-gray-900 dark:text-gray-100">Auto-deploy new versions</Text>
                        </View>
                      }
                      value={slowRolloutAutoDeploy}
                      onChange={setSlowRolloutAutoDeploy}
                      className="!w-9 !h-9"
                    />
                    <InputField
                      label="Rollout Percent"
                      type="number"
                      value={slowRolloutPercent}
                      onChange={(v) => setSlowRolloutPercent(Math.max(0, Math.min(100, Number(v) || 0)))}
                      suffix={<Text className="text-xs text-gray-500">%</Text>}
                    />
                  </View>
                )}
                <InputField
                  label="Notify Emails (comma separated)"
                  placeholder="name@example.com, team@company.com"
                  value={notifyEmails}
                  onChange={setNotifyEmails}
                />
                <InputField
                  label="Focus Prompt"
                  type="textarea"
                  value={focusPrompt}
                  onChange={setFocusPrompt}
                  placeholder="What should the optimizer focus on?"
                />
                <DropdownField
                  label="Set Active Version"
                  options={(optimizerDetails.functionIds || [])
                    .filter((fid: string) => agentFunctions.some((f) => f.id === fid) || functionNameMap[fid])
                    .map((fid: string) => ({
                      label: <Text className="text-gray-900 dark:text-gray-100">{functionIdToName(fid)}</Text>,
                      value: fid,
                    }))}
                  value={selectedFunctionId}
                  onChange={setSelectedFunctionId}
                  placeholder="Select a version to deploy"
                  readOnly={isFunctionsLoading}
                />
                <Text className="text-xs text-gray-500 dark:text-gray-400">Select a version to deploy it to this environment.</Text>
                <View className="mt-2">
                  <View className="flex items-center justify-between">
                    <Text className="text-sm font-semibold">Version History</Text>
                  </View>
                  <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    The optimizer creates new function versions as it improves your agent. Use the dropdown above to switch versions.
                  </Text>
                  <View className="mt-2 flex flex-col gap-1 max-h-56 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded p-2">
                    {(optimizerDetails.functionIds || [])
                      .filter((fid: string) => agentFunctions.some((f) => f.id === fid) || functionNameMap[fid])
                      .slice()
                      .reverse()
                      .map((fid: string, idx: number, arr: string[]) => {
                        const isCurrent = optimizerDetails.currentFunctionId === fid;
                        const currentFunctionIndex = arr.findIndex((f) => f === optimizerDetails.currentFunctionId);
                        const shouldGreyOut = currentFunctionIndex !== -1 && idx < currentFunctionIndex;
                        const funcName = functionIdToName(fid);
                        const displayName = funcName === fid ? `Version ${arr.length - idx}` : funcName;
                        return (
                          <View
                            key={fid}
                            className={`flex items-center text-sm gap-2 p-2 rounded ${shouldGreyOut ? "opacity-50" : ""}`}
                          >
                            <Text className={`truncate ${shouldGreyOut ? "text-gray-400 dark:text-gray-600" : ""}`}>{displayName}</Text>
                            {isCurrent && <Text className="text-xs text-green-600 dark:text-green-400">deployed</Text>}
                          </View>
                        );
                      })}
                  </View>
                </View>
              </>
            )}
          </View>
          <View className="border-t border-gray-200 dark:border-gray-700 p-4">
            <View className="flex justify-between">
              <Button
                variant="danger"
                size="auto"
                onClick={async () => {
                  if (!agentId || !currentEnvironment?.optimizerId || !environmentId) return;
                  try {
                    // Deactivate the optimizer
                    await updateOptimizer(`/v1/agents/${agentId}/optimizers/${currentEnvironment.optimizerId}`, {
                      method: "PATCH",
                      body: { active: false },
                    });
                    // Clear the optimizer reference on the environment
                    dispatch(
                      agentEnvironmentActions.updateById({
                        id: environmentId,
                        agentId,
                        optimizerId: null,
                      } as any),
                    );
                    dispatch(agentEnvironmentActions.get({ agentId } as any) as any);
                    setShowViewOptimizerDialog(false);
                  } catch (error) {
                    console.error("Failed to deactivate optimizer:", error);
                  }
                }}
                disabled={isUpdatingOptimizer}
              >
                Turn Off
              </Button>
              <View className="flex gap-2">
                <Button
                  variant="neutral"
                  size="auto"
                  onClick={() => setShowViewOptimizerDialog(false)}
                >
                  Close
                </Button>
                <Button
                  variant="info"
                  size="auto"
                  onClick={async () => {
                    if (!agentId || !currentEnvironment?.optimizerId) return;
                    try {
                      const body: Record<string, any> = {
                        approvalMode,
                        notifyEmails: notifyEmails
                          .split(",")
                          .map((e) => e.trim())
                          .filter(Boolean),
                        focusPrompt: focusPrompt || undefined,
                      };
                      if (approvalMode === "slow_rollout") {
                        body.approvalData = {
                          rolloutPercent: Math.max(0, Math.min(1, (Number(slowRolloutPercent) || 0) / 100)),
                          autoDeploy: !!slowRolloutAutoDeploy,
                          structureCreated: (optimizerDetails.approvalData?.structureCreated as boolean) || false,
                        };
                      }
                      if (selectedFunctionId) {
                        body.currentFunctionId = selectedFunctionId;
                        const currentFunctionIds = optimizerDetails.functionIds || [];
                        if (!currentFunctionIds.includes(selectedFunctionId)) {
                          body.functionIds = [...currentFunctionIds, selectedFunctionId];
                        }
                      }
                      await updateOptimizer(`/v1/agents/${agentId}/optimizers/${currentEnvironment.optimizerId}`, {
                        method: "PATCH",
                        body,
                      });
                      // Refresh environments to reflect any function deployment changes
                      dispatch(agentEnvironmentActions.get({ agentId } as any) as any);
                      setShowViewOptimizerDialog(false);
                    } catch (error) {
                      console.error("Failed to update optimizer:", error);
                    }
                  }}
                  disabled={isUpdatingOptimizer}
                >
                  {isUpdatingOptimizer ? "Saving..." : "Save"}
                </Button>
              </View>
            </View>
          </View>
        </View>
      </Dialog>
    </View>
  );
};
